'use client';

import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight, Zap, Shield, TrendingUp, Users } from 'lucide-react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { createClient } from '@/lib/supabase/client';
import ThemeToggle from '@/components/ThemeToggle';

type AuthMode = 'login' | 'signup';
type UserRole = 'business' | 'blower';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface SignupFormData {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  role: UserRole;
  agreeTerms: boolean;
}

const platformStats = [
  { label: 'Active Blowers', value: '12,400+', icon: Users },
  { label: 'Drops Created', value: '3,800+', icon: Zap },
  { label: 'Paid Out', value: '₦480M+', icon: TrendingUp },
  { label: 'Verified Posts', value: '94,200+', icon: Shield },
];

export default function SignUpLoginContent() {
  const searchParams = useSearchParams();
  const [mode, setMode] = useState<AuthMode>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserRole>('business');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const authError = searchParams.get('auth_error');
    if (authError) {
      toast.error(decodeURIComponent(authError));
      window.history.replaceState({}, '', '/');
    }
  }, [searchParams]);

  const loginForm = useForm<LoginFormData>({
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const signupForm = useForm<SignupFormData>({
    defaultValues: { fullName: '', email: '', password: '', confirmPassword: '', role: 'business', agreeTerms: false },
  });

  const onLoginSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: data.email, password: data.password }),
      });
      const result = await res.json();
      if (!res.ok) {
        toast.error(result.error || 'Invalid credentials. Please try again.');
        setIsLoading(false);
        return;
      }

      if (result.session) {
        const supabase = createClient();
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: result.session.access_token,
          refresh_token: result.session.refresh_token,
        });
        if (sessionError) {
          toast.error('Could not restore session. Please try again.');
          setIsLoading(false);
          return;
        }
      }

      toast.success('Welcome back! Redirecting...');
      const role = result.user?.user_metadata?.role || result.profile?.role;
      const destination =
        role === 'business' ?'/business-dashboard'
          : role === 'blower' ?'/drop-board'
            : role === 'admin' ?'/admin/dashboard'
              : role === 'inspector' ?'/inspector' :'/business-dashboard';
      window.location.href = destination;
    } catch {
      toast.error('Something went wrong. Please try again.');
      setIsLoading(false);
    }
  };

  const onSignupSubmit = async (data: SignupFormData) => {
    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setIsLoading(true);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      const res = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: data.fullName,
          email: data.email,
          password: data.password,
          role: data.role,
        }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      const result = await res.json();
      if (!res.ok) {
        toast.error(result.error || 'Signup failed. Please try again.');
        return;
      }

      toast.success('Account created! Please verify your email.');
      const uid = result.userId || '';
      window.location.href = `/verify-email?email=${encodeURIComponent(data.email)}&user_id=${encodeURIComponent(uid)}`;
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        toast.error('Signup timed out. Your account may have been created — try logging in or contact support.');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    try {
      // IMPORTANT: OAuth must be initiated from the BROWSER client so the PKCE
      // code_verifier is stored in this browser (cookie/localStorage). If we
      // initiate it server-side (as the old /api/auth/google route did), the
      // verifier lives on the server and `exchangeCodeForSession` on the
      // callback page fails — which is why users were bounced back to login.
      const supabase = createClient();
      // Use the CURRENT origin so the PKCE code_verifier cookie (set on this
      // domain) is readable when we return to /auth/callback. Hardcoding a
      // single site URL breaks OAuth when the app is opened on a different
      // domain (e.g. a Netlify deploy-preview URL), because the verifier cookie
      // lives on the origin the flow started from.
      const siteUrl =
        (typeof window !== 'undefined' ? window.location.origin : '') ||
        process.env.NEXT_PUBLIC_SITE_URL ||
        '';

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${siteUrl}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });

      if (error) {
        toast.error(error.message || 'Google sign-in failed. Please try again.');
      }
      // On success the browser is redirected to Google automatically.
    } catch {
      toast.error('Google sign-in failed. Please try again.');
    }
  };

  // Remove the googleOAuthEnabled gate — Supabase handles OAuth server-side,
  // no NEXT_PUBLIC_GOOGLE_CLIENT_ID env var is needed on the client.

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left panel — Brand */}
      <div className="hidden lg:flex lg:w-[52%] xl:w-[55%] relative flex-col overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-[#0D0020] to-black" />
        <div className="absolute inset-0 bg-gradient-radial-purple" />
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'linear-gradient(rgba(139,47,201,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(139,47,201,0.3) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
        <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-primary/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 right-1/4 w-64 h-64 bg-accent/10 rounded-full blur-3xl" />

        <div className="relative z-10 flex flex-col h-full p-12">
          <div className="flex items-center gap-3">
            <AppLogo size={140} className="py-1" />
          </div>

          <div className="flex-1 flex flex-col justify-center">
            <div className="mb-6">
              <span className="inline-flex items-center gap-2 bg-primary/15 border border-primary/30 text-accent text-xs font-600 px-3 py-1.5 rounded-full mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-accent pulse-purple" />
                Nigeria&apos;s Verified Visibility Network
              </span>
            </div>

            <h1 className="text-hero-display text-white mb-6">
              Get your brand{' '}
              <span className="text-gradient-purple">seen</span>{' '}
              by millions.
            </h1>

            <p className="text-lg text-muted-foreground leading-relaxed mb-10 max-w-md">
              Post a Drop. Verified Blowers pick it up. The Inspector system confirms every post.
              Your money only moves when the work is done.
            </p>

            <div className="grid grid-cols-2 gap-4">
              {platformStats.map((stat) => {
                const StatIcon = stat.icon;
                return (
                  <div
                    key={`stat-${stat.label}`}
                    className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl p-4 backdrop-blur-sm"
                  >
                    <div className="w-9 h-9 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <StatIcon size={16} className="text-accent" />
                    </div>
                    <div>
                      <p className="text-lg font-700 text-white">{stat.value}</p>
                      <p className="text-xs text-muted-foreground">{stat.label}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm">
            <p className="text-sm text-foreground/80 leading-relaxed mb-4">
              &ldquo;BLOW paid me ₦180,000 in my first month. The Inspector system caught a fake submission attempt
              from another creator and protected the brand. This platform is built different.&rdquo;
            </p>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-gradient-purple flex items-center justify-center text-white text-sm font-700">
                CE
              </div>
              <div>
                <p className="text-sm font-600 text-foreground">Chisom Ezenwachi</p>
                <p className="text-xs text-muted-foreground">TikTok Blower · Lagos</p>
              </div>
              <div className="ml-auto flex gap-0.5">
                {[1, 2, 3, 4, 5].map((s) => (
                  <span key={`star-${s}`} className="text-warning text-sm">★</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel — Auth form */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 py-8 sm:py-12 overflow-y-auto relative">
        {/* Theme toggle — top right */}
        <div className="absolute top-4 right-4">
          <ThemeToggle />
        </div>

        {/* Mobile logo */}
        <div className="flex lg:hidden items-center gap-2 mb-8">
          <AppLogo size={110} className="py-1" />
        </div>

        <div className="w-full max-w-md fade-in">
          {/* Tab switcher */}
          <div className="flex bg-muted rounded-xl p-1 mb-8">
            <button
              onClick={() => setMode('login')}
              className={`flex-1 py-2.5 text-sm font-600 rounded-lg transition-all duration-200 ${
                mode === 'login' ? 'bg-primary text-white shadow-glow-purple-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Log In
            </button>
            <button
              onClick={() => setMode('signup')}
              className={`flex-1 py-2.5 text-sm font-600 rounded-lg transition-all duration-200 ${
                mode === 'signup' ? 'bg-primary text-white shadow-glow-purple-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Sign Up
            </button>
          </div>

          {/* Form header */}
          <div className="mb-6">
            <h2 className="text-3xl font-700 text-foreground mb-1">
              {mode === 'login' ? 'Welcome back' : 'Create your account'}
            </h2>
            <p className="text-sm text-muted-foreground">
              {mode === 'login' ? 'Log in to your BLOW account to continue' : "Join Nigeria's fastest-growing creator network"}
            </p>
          </div>

          {/* Google OAuth */}
          <button
            onClick={handleGoogleAuth}
            className="w-full flex items-center justify-center gap-3 bg-muted border border-border rounded-xl py-3 px-4 text-sm font-500 text-foreground hover:border-primary/40 hover:bg-muted/80 transition-all duration-150 mb-6 active:scale-[0.98] min-h-[44px]"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
              <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
              <path d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
              <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground">or continue with email</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* LOGIN FORM */}
          {mode === 'login' && (
            <form onSubmit={loginForm.handleSubmit(onLoginSubmit)} className="space-y-4">
              <div>
                <label htmlFor="login-email" className="block text-sm font-500 text-foreground mb-1.5">
                  Email address
                </label>
                <input
                  id="login-email"
                  type="email"
                  placeholder="you@example.com"
                  {...loginForm.register('email', {
                    required: 'Email is required',
                    pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' },
                  })}
                  className="w-full bg-input border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-150 min-h-[44px]"
                />
                {loginForm.formState.errors.email && (
                  <p className="mt-1 text-xs text-error">{loginForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label htmlFor="login-password" className="text-sm font-500 text-foreground">
                    Password
                  </label>
                  <Link href="/forgot-password" className="text-xs text-accent hover:text-primary transition-colors">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    {...loginForm.register('password', {
                      required: 'Password is required',
                      minLength: { value: 8, message: 'Password must be at least 8 characters' },
                    })}
                    className="w-full bg-input border border-border rounded-xl px-4 py-3 pr-11 text-sm text-foreground placeholder-muted-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-150 min-h-[44px]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="mt-1 text-xs text-error">{loginForm.formState.errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="remember-me"
                  type="checkbox"
                  {...loginForm.register('rememberMe')}
                  className="w-4 h-4 rounded border-border bg-input accent-primary"
                />
                <label htmlFor="remember-me" className="text-sm text-muted-foreground">
                  Remember me for 30 days
                </label>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full flex items-center justify-center gap-2 rounded-xl py-3.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Signing in...
                  </>
                ) : (
                  <>
                    Log In to BLOW
                    <ArrowRight size={16} />
                  </>
                )}
              </button>

              <p className="text-center text-sm text-muted-foreground">
                Don&apos;t have an account?{' '}
                <button
                  type="button"
                  onClick={() => setMode('signup')}
                  className="text-accent hover:text-primary transition-colors font-500"
                >
                  Sign up free
                </button>
              </p>
            </form>
          )}

          {/* SIGNUP FORM */}
          {mode === 'signup' && (
            <form onSubmit={signupForm.handleSubmit(onSignupSubmit)} className="space-y-4">
              <div>
                <label className="block text-sm font-500 text-foreground mb-2">
                  I am joining as a...
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {(['business', 'blower'] as UserRole[]).map((role) => (
                    <button
                      key={`role-${role}`}
                      type="button"
                      onClick={() => {
                        setSelectedRole(role);
                        signupForm.setValue('role', role);
                      }}
                      className={`
                        flex flex-col items-center gap-2 p-4 rounded-xl border-2 text-sm font-500 transition-all duration-150
                        ${selectedRole === role
                          ? 'border-primary bg-primary/10 text-accent' :'border-border bg-input text-muted-foreground hover:border-primary/40 hover:text-foreground'
                        }
                      `}
                    >
                      {role === 'business' ? (
                        <>
                          <span className="text-2xl">🏢</span>
                          <span>Business</span>
                          <span className="text-xs text-muted-foreground text-center">Post Drops &amp; fund campaigns</span>
                        </>
                      ) : (
                        <>
                          <span className="text-2xl">⚡</span>
                          <span>Blower</span>
                          <span className="text-xs text-muted-foreground text-center">Create content &amp; get paid</span>
                        </>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="signup-name" className="block text-sm font-500 text-foreground mb-1.5">
                  Full name
                </label>
                <input
                  id="signup-name"
                  type="text"
                  placeholder="Amaka Okonkwo"
                  {...signupForm.register('fullName', {
                    required: 'Full name is required',
                    minLength: { value: 2, message: 'Name must be at least 2 characters' },
                  })}
                  className="w-full bg-input border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-150 min-h-[44px]"
                />
                {signupForm.formState.errors.fullName && (
                  <p className="mt-1 text-xs text-error">{signupForm.formState.errors.fullName.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="signup-email" className="block text-sm font-500 text-foreground mb-1.5">
                  Email address
                </label>
                <input
                  id="signup-email"
                  type="email"
                  placeholder="you@company.ng"
                  {...signupForm.register('email', {
                    required: 'Email is required',
                    pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' },
                  })}
                  className="w-full bg-input border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-150 min-h-[44px]"
                />
                {signupForm.formState.errors.email && (
                  <p className="mt-1 text-xs text-error">{signupForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="signup-password" className="block text-sm font-500 text-foreground mb-1.5">
                  Password
                </label>
                <p className="text-xs text-muted-foreground mb-1.5">Minimum 8 characters with a number and symbol</p>
                <div className="relative">
                  <input
                    id="signup-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create a strong password"
                    {...signupForm.register('password', {
                      required: 'Password is required',
                      minLength: { value: 8, message: 'Must be at least 8 characters' },
                      pattern: {
                        value: /^(?=.*[0-9])(?=.*[!@#$%^&*])/,
                        message: 'Must contain a number and a symbol',
                      },
                    })}
                    className="w-full bg-input border border-border rounded-xl px-4 py-3 pr-11 text-sm text-foreground placeholder-muted-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-150 min-h-[44px]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {signupForm.formState.errors.password && (
                  <p className="mt-1 text-xs text-error">{signupForm.formState.errors.password.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="signup-confirm" className="block text-sm font-500 text-foreground mb-1.5">
                  Confirm password
                </label>
                <div className="relative">
                  <input
                    id="signup-confirm"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Re-enter your password"
                    {...signupForm.register('confirmPassword', { required: 'Please confirm your password' })}
                    className="w-full bg-input border border-border rounded-xl px-4 py-3 pr-11 text-sm text-foreground placeholder-muted-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-150 min-h-[44px]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {signupForm.formState.errors.confirmPassword && (
                  <p className="mt-1 text-xs text-error">{signupForm.formState.errors.confirmPassword.message}</p>
                )}
              </div>

              <div className="flex items-start gap-2">
                <input
                  id="agree-terms"
                  type="checkbox"
                  {...signupForm.register('agreeTerms', { required: 'You must agree to continue' })}
                  className="w-4 h-4 mt-0.5 rounded border-border bg-input accent-primary flex-shrink-0"
                />
                <label htmlFor="agree-terms" className="text-sm text-muted-foreground leading-relaxed">
                  I agree to BLOW&apos;s{' '}
                  <span className="text-accent hover:underline cursor-pointer">Terms of Service</span>{' '}
                  and{' '}
                  <span className="text-accent hover:underline cursor-pointer">Privacy Policy</span>
                </label>
              </div>
              {signupForm.formState.errors.agreeTerms && (
                <p className="text-xs text-error">{signupForm.formState.errors.agreeTerms.message}</p>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full flex items-center justify-center gap-2 rounded-xl py-3.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Creating account...
                  </>
                ) : (
                  <>
                    Create BLOW Account
                    <ArrowRight size={16} />
                  </>
                )}
              </button>

              <p className="text-center text-sm text-muted-foreground">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => setMode('login')}
                  className="text-accent hover:text-primary transition-colors font-500"
                >
                  Log in
                </button>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}